import { qboTxnUrl } from '@/utils/qboLinks'

export function amazonReportLocksTypeAndVendor(sourceType: string | undefined | null): boolean {
  return sourceType === 'amazon_report'
}

export function hasAmazonPreviewPayload(
  extractedData: Record<string, unknown> | null | undefined,
): boolean {
  const amazon = extractedData?.amazon
  return amazon != null && typeof amazon === 'object' && !Array.isArray(amazon)
}

export type DocumentPreviewMode = 'pdf' | 'email' | 'ocr' | 'amazon'

export function pickDefaultDocumentPreviewMode(doc: {
  pdf_storage_path?: string | null
  source_type?: string
  raw_ocr_text?: string | null
  extracted_data?: Record<string, unknown> | null
}): DocumentPreviewMode {
  if (hasAmazonPreviewPayload(doc.extracted_data)) return 'amazon'
  if (doc.pdf_storage_path) return 'pdf'
  if (doc.source_type === 'email') return 'email'
  if (doc.raw_ocr_text) return 'ocr'
  return 'pdf'
}

export function lineItemHeaderGridClass(isReadOnly: boolean, showAmazonQtyUnit: boolean): string {
  if (showAmazonQtyUnit) {
    return isReadOnly
      ? 'grid-cols-[minmax(130px,1.2fr)_minmax(90px,1fr)_48px_56px_65px]'
      : 'grid-cols-[20px_24px_minmax(130px,1.2fr)_minmax(90px,1fr)_48px_56px_65px_56px]'
  }
  return isReadOnly
    ? 'grid-cols-[minmax(130px,1.2fr)_minmax(90px,1fr)_65px]'
    : 'grid-cols-[20px_24px_minmax(130px,1.2fr)_minmax(90px,1fr)_65px_56px]'
}

export type AmazonQboDuplicateMessage = {
  text: string
  qboUrl: string | null
  confirmText: string
}

/** Copy for Amazon Business QBO Purchase duplicate (Intuit expense on order, not charge id). */
export function amazonQboDuplicateMessage(
  extractedData: Record<string, unknown> | null | undefined,
  options: { total: string; issueDate: string },
): AmazonQboDuplicateMessage {
  const amazonRaw = extractedData?.amazon
  const amazon =
    amazonRaw != null && typeof amazonRaw === 'object' && !Array.isArray(amazonRaw)
      ? (amazonRaw as Record<string, unknown>)
      : null
  const orderId =
    typeof amazon?.order_id === 'string' && amazon.order_id.trim()
      ? amazon.order_id.trim()
      : '—'
  const qboEntityId =
    typeof extractedData?.qbo_duplicate_entity_id === 'string'
      ? extractedData.qbo_duplicate_entity_id
      : null
  const qboUrl = qboEntityId ? qboTxnUrl('Purchase', qboEntityId) : null
  const txnDate =
    typeof extractedData?.qbo_duplicate_txn_date === 'string' &&
    extractedData.qbo_duplicate_txn_date.trim()
      ? extractedData.qbo_duplicate_txn_date.trim()
      : options.issueDate || '—'
  const amount =
    typeof extractedData?.qbo_duplicate_total === 'string' &&
    extractedData.qbo_duplicate_total.trim()
      ? extractedData.qbo_duplicate_total.trim()
      : options.total
  const refNo =
    typeof extractedData?.qbo_duplicate_doc_number === 'string' &&
    extractedData.qbo_duplicate_doc_number.trim()
      ? extractedData.qbo_duplicate_doc_number.trim()
      : null

  let text = `QuickBooks already has a ${amount} expense for Amazon order ${orderId} (txn date ${txnDate}`
  if (refNo) {
    text += `, Ref no. ${refNo}`
  }
  text +=
    '). That match is the order in QuickBooks, not the Charge Identifier / Reference # on this document. Approve is blocked until you change the duplicate in QuickBooks or confirm to post another expense anyway.'

  const confirmText = `QuickBooks already has a ${amount} expense for Amazon order ${orderId}. Post anyway? Approving will create another expense in QuickBooks.`

  return { text, qboUrl, confirmText }
}

export function resolveApproveButtonLabel(
  sourceType: string | undefined | null,
  docType: string | undefined | null,
): string {
  if (amazonReportLocksTypeAndVendor(sourceType)) return 'Approve'
  if (docType === 'card_expense') return 'Send to QuickBooks as Expense'
  if (docType === 'card_credit') return 'Send to QuickBooks as Card credit'
  return 'Approve'
}
